import type { Field, FieldNode, Page, SetWhen } from '@fieldia/core';
import type { ElementFactory } from './chrome';
import type { DesignerWords } from './designer-words';
import type { Designer } from './designer';
import { settingDate, type SettingDate } from './date-setting';
import { plainNumber, settingNumber, type SettingNumber } from './number-box';
import { findNode } from './page-tree';
import { cannotHold } from './rules-commands';
import { formulaBox } from './rules-formula-box';
import { formulaAdvice, formulaProblem, literalOf, problemWords, sampleResult, wouldCircle } from './rules-formula';
import { pageRules } from './rules-words';

/**
 * A field's value from others, on the Rules tab. "Worked out from": a
 * formula, typed with the page's fields suggested by their labels, a short
 * list of functions put in where the cursor is, and the result on made-up
 * values; the field is then read-only, which the form already makes it.
 * "Set when…": values set when something holds, each a sentence that opens
 * in place to the value and when, as the answer rules do.
 */

/** The kinds a formula is offered for: numbers, amounts and text. */
const WORKED_OUT = new Set(['char', 'text', 'integer', 'float', 'monetary']);
/** The kinds a value can be set for by a rule: those, choices of one, yes or no, and dates. */
const SET = new Set([...WORKED_OUT, 'selection', 'boolean', 'date', 'datetime']);

type FunctionName = keyof DesignerWords['rulesUi']['functionTips'];
type FunctionGroup = keyof DesignerWords['rulesUi']['functionGroups'];

/** The functions offered, by what they work on, each with what is put in and where the cursor goes: the first value to type. */
const FUNCTIONS: [FunctionGroup, { name: FunctionName; text: string; back: number }[]][] = [
  [
    'numbers',
    [
      { name: 'round', text: 'round(, 2)', back: 4 },
      { name: 'roundMoney', text: 'roundMoney(, )', back: 3 },
      { name: 'min', text: 'min(, )', back: 3 },
      { name: 'max', text: 'max(, )', back: 3 },
      { name: 'abs', text: 'abs()', back: 1 },
      { name: 'sum', text: "sum(, '')", back: 5 },
      { name: 'count', text: 'count()', back: 1 },
      { name: 'average', text: 'average()', back: 1 },
    ],
  ],
  [
    'dates',
    [
      { name: 'today', text: 'today()', back: 0 },
      { name: 'days', text: 'days(, today())', back: 10 },
      { name: 'age', text: 'age()', back: 1 },
      { name: 'years', text: 'years(, today())', back: 10 },
      { name: 'months', text: 'months(, today())', back: 10 },
      { name: 'addDays', text: 'addDays(, 1)', back: 4 },
      { name: 'addMonths', text: 'addMonths(, 1)', back: 4 },
      { name: 'addYears', text: 'addYears(, 1)', back: 4 },
    ],
  ],
  [
    'words',
    [
      { name: 'contains', text: "contains(, '')", back: 5 },
      { name: 'startsWith', text: "startsWith(, '')", back: 5 },
      { name: 'endsWith', text: "endsWith(, '')", back: 5 },
      { name: 'upper', text: 'upper()', back: 1 },
      { name: 'lower', text: 'lower()', back: 1 },
      { name: 'join', text: "join(' ', )", back: 1 },
      { name: 'len', text: 'len()', back: 1 },
    ],
  ],
  [
    'choosing',
    [
      { name: 'if', text: 'if(, , )', back: 5 },
      { name: 'empty', text: 'empty()', back: 1 },
      { name: 'firstOf', text: 'firstOf(, )', back: 3 },
    ],
  ],
];

interface Own {
  node: FieldNode;
  name: string;
  def: Field;
}

function ownOf(page: Page, id: string): Own | null {
  const found = findNode(page, id)?.node;
  if (found?.type !== 'field' || !page.fields[found.field]) return null;
  return { node: found, name: found.field, def: page.fields[found.field] };
}

export interface RulesSetting {
  element: HTMLElement;
  update(page: Page): void;
}

// ---- worked out from ------------------------------------------------------------------------------

export function workedOutSetting(el: ElementFactory, designer: Designer, id: string): RulesSetting {
  const words = designer.words;
  const w = words.rulesUi;
  let own: Own | null = null;
  const box = formulaBox(el, {
    words,
    label: w.workedOutFrom,
    placeholder: w.typeAName,
    own: () => own?.name,
    check: (page, source) => formulaProblem(page, source, words, designer.functions()) ?? (own && wouldCircle(page, own.name, source) ? { words: wouldCircle(page, own.name, source, words) as string } : null),
    commit(source) {
      const was = own?.def.compute ?? '';
      if (source === was) return;
      designer.setCompute(id, source || null);
    },
    // A value of a kind a function does not take is said in the result's place: kept, it gives nothing there.
    said: (page, source) => {
      const advice = formulaAdvice(page, source, words, designer.functions());
      if (advice) return { warn: problemWords(advice, words) };
      return own ? sampleResult(page, own.name, source, words, designer.functions()) : '';
    },
  });
  /** A function's button: its tip on pointing at it, and put in where the cursor is. */
  const offer = (name: string, tip: string, text: string, back: number) => {
    const button = el('button', { type: 'button', class: 'fd-formula-function', title: tip }, name);
    // The box keeps its cursor: the function goes in where it was.
    button.addEventListener('mousedown', (event) => event.preventDefault());
    button.addEventListener('click', () => box.insert(text, back));
    return button;
  };
  const group = (title: string, buttons: HTMLElement[]) => {
    const heading = el('span', { class: 'fd-formula-group-name' }, title);
    return el('div', { class: 'fd-formula-group', role: 'group', 'aria-label': title }, heading, ...buttons);
  };
  // The app's own, after Fieldia's, each with the words it gave.
  const appFunctions = Object.entries(designer.functions() ?? {}).map(([name, entry]) => {
    const tip = typeof entry === 'function' ? undefined : entry.tip;
    return offer(name, tip ?? w.appFunctionTip(name), `${name}()`, 1);
  });
  const functions = el(
    'div',
    { class: 'fd-formula-functions', role: 'group', 'aria-label': w.functions },
    ...FUNCTIONS.map(([title, list]) => group(w.functionGroups[title], list.map((fn) => offer(fn.name, w.functionTips[fn.name], fn.text, fn.back)))),
    ...(appFunctions.length ? [group(w.functionGroups.app, appFunctions)] : [])
  );
  const hint = el('p', { class: 'fd-properties-hint fd-set-hint' });
  const element = el('div', { class: 'fd-prop fd-worked-out', 'data-tab': 'rules', 'data-setting': 'Worked out from' }, el('span', { class: 'fd-prop-name' }, w.workedOutFrom), box.element, functions, box.problem, box.result, hint);
  return {
    element,
    update(page) {
      own = ownOf(page, id);
      element.hidden = !own || !WORKED_OUT.has(own.def.type) || designer.isFromModel(id);
      if (!own || element.hidden) return;
      box.update(page, own.def.compute ?? '');
      hint.textContent = own.def.compute ? w.computedHint : w.computeHint;
    },
  };
}

// ---- set when -------------------------------------------------------------------------------------

/** The kinds whose value is typed into the designer's number box. */
const NUMBERS = new Set(['integer', 'float', 'monetary']);
/** The kinds whose value is typed into the designer's date box. */
const DATES = new Set(['date', 'datetime']);

/**
 * A value as the formula language writes it, from what a control holds; null while it holds nothing. A number or a
 * date is never read here: its own box reads it (FIELDIA INPUT RULES), a number written plainly (`plainNumber`), a date
 * as its field keeps it.
 */
function literalFrom(def: Field, raw: string): string | null {
  if (raw === '' || NUMBERS.has(def.type) || DATES.has(def.type)) return null;
  switch (def.type) {
    case 'boolean':
      return raw === 'True' || raw === 'False' ? raw : null;
    case 'selection': {
      const option = def.options.find((o) => String(o.value) === raw);
      if (!option) return null;
      return typeof option.value === 'number' ? String(option.value) : `'${option.value.replace(/'/g, '')}'`;
    }
    default:
      // A quote cannot sit inside text in a formula: it is left out.
      return `'${raw.replace(/'/g, '')}'`;
  }
}

/** What a control shows for a value as written: a literal by its value, anything else as the formula it is. */
function rawFrom(value: string): string {
  const literal = literalOf(value);
  if (!literal) return value;
  if (literal.value === true) return 'True';
  if (literal.value === false) return 'False';
  return literal.value === null ? '' : String(literal.value);
}

export function setWhenSetting(el: ElementFactory, designer: Designer, id: string): RulesSetting {
  const words = designer.words;
  const w = words.rulesUi;
  const list = el('ul', { class: 'fd-answer-rules fd-set-when-list', 'aria-label': w.valuesSetWhen });
  const add = el('button', { type: 'button', class: 'fd-button fd-button-link fd-answer-rules-add' }, w.setWhenAdd);
  const hint = el('p', { class: 'fd-properties-hint fd-set-hint' }, w.setWhenHint);
  const element = el('div', { class: 'fd-prop fd-answer-rules-box fd-set-when', 'data-tab': 'rules', 'data-setting': 'Set when' }, el('span', { class: 'fd-prop-name' }, w.setWhen), list, add, hint);
  let page = designer.getPage();
  let own: Own | null = null;
  let rows: Row[] = [];
  /** A row begun with Set when…, not kept until both its parts read. */
  let drafting = false;

  const stored = (): SetWhen[] => own?.def.setWhen ?? [];

  add.addEventListener('click', () => {
    drafting = true;
    draw();
    rows[rows.length - 1]?.open(true);
    rows[rows.length - 1]?.focusFirst();
  });

  interface Row {
    element: HTMLLIElement;
    kind: string;
    update(item: SetWhen | null): void;
    open(on: boolean): void;
    focusFirst(): void;
  }

  function row(index: number, def: Field, raw: boolean): Row {
    const say = el('button', { type: 'button', class: 'fd-answer-rule-say fd-set-when-say', 'aria-expanded': 'false' });
    const remove = el('button', { type: 'button', class: 'fd-icon-button fd-answer-rule-remove', title: w.remove }, '×');
    const problem = el('p', { class: 'fd-answer-rule-problem', role: 'alert', hidden: '' });
    let value: HTMLInputElement | HTMLSelectElement;
    /**
     * A number's value: the designer's number box, never the browser's, which follows the browser's language,
     * reads "1,5" as 15 and empties itself on Arabic digits. What it keeps, as it is left, is the row's number.
     */
    let number: SettingNumber | null = null;
    let typedNumber: number | null = null;
    /**
     * A date's value, or a date and time's: the designer's date box, never the browser's, which follows the browser's
     * language and reads no Arabic digits. What it keeps, as it is left, is the row's day (or day and time).
     */
    let date: SettingDate | null = null;
    let typedDate: string | null = null;
    if (raw) value = el('input', { class: 'fd-input fd-answer-rule-code', 'aria-label': w.setTo, spellcheck: 'false', autocomplete: 'off' });
    else if (def.type === 'selection' || def.type === 'boolean') {
      const options = def.type === 'boolean' ? [{ value: 'True', label: w.yes }, { value: 'False', label: w.no }] : def.options.map((o) => ({ value: String(o.value), label: o.label }));
      value = el('select', { class: 'fd-input fd-select', 'aria-label': w.setTo }, el('option', { value: '' }, w.choose), ...options.map((o) => el('option', { value: o.value }, o.label)));
    } else if (NUMBERS.has(def.type)) {
      number = settingNumber(el, designer, {
        label: w.setTo,
        attrs: { class: 'fd-input' },
        integer: def.type === 'integer',
        optional: true,
        saved: () => typedNumber,
        commit(n) {
          typedNumber = n;
          save();
        },
      });
      value = number.input;
    } else if (DATES.has(def.type)) {
      date = settingDate(el, designer, {
        kind: def.type === 'datetime' ? 'datetime' : 'date',
        label: w.setTo,
        saved: () => typedDate,
        commit(day) {
          typedDate = day;
          save();
        },
      });
      value = date.input;
    } else value = el('input', { class: 'fd-input', type: 'text', 'aria-label': w.setTo, autocomplete: 'off' });
    let when = '';
    // As the condition starts to hold, or when a field changes (Flectra's onchange): the condition is then optional.
    const as = el('select', { class: 'fd-input fd-select fd-set-when-as', 'aria-label': w.setAs }, el('option', { value: '' }, w.asItStartsToHold)) as HTMLSelectElement;
    let offered = '';
    as.addEventListener('change', () => {
      whenBox.input.placeholder = as.value ? w.ifPlaceholder : w.whenPlaceholder;
      save();
    });
    const whenBox = formulaBox(el, {
      words,
      label: w.when,
      placeholder: w.whenPlaceholder,
      check: (p, source) => formulaProblem(p, source, words, designer.functions()),
      commit(source) {
        when = source;
        save();
      },
    });
    const body = el(
      'div',
      { class: 'fd-answer-rule-body', hidden: '' },
      el('label', { class: 'fd-answer-rule-field' }, el('span', { class: 'fd-answer-rule-word' }, w.setTo), value),
      el('label', { class: 'fd-answer-rule-field' }, el('span', { class: 'fd-answer-rule-word' }, w.setAs), as),
      el('div', { class: 'fd-answer-rule-field' }, el('span', { class: 'fd-answer-rule-word' }, w.when), whenBox.element, whenBox.problem),
      problem
    );
    const element = el('li', { class: 'fd-answer-rule' }, el('div', { class: 'fd-answer-rule-head' }, say, remove), body);

    /** Kept once both parts read: the value one the field can hold, when a condition that reads. */
    function save() {
      if (!own) return;
      const expression = raw ? value.value.trim() : number ? (typedNumber === null ? null : plainNumber(typedNumber)) : date ? (typedDate === null ? null : `'${typedDate}'`) : literalFrom(own.def, value.value);
      const wrong = !expression ? null : raw ? formulaProblem(page, expression, words, designer.functions())?.words ?? null : cannotHold(own.def, own.node.label ?? own.def.label, expression, words);
      problem.textContent = wrong ?? '';
      problem.hidden = !wrong;
      if (!expression || !(when || as.value) || wrong) return;
      const items = [...stored()];
      // Several fields it is on, from the page as written: the first is the one shown, the rest kept.
      items[index] = { ...(when ? { when } : {}), value: expression, ...(as.value ? { on: [as.value, ...(stored()[index]?.on?.slice(1) ?? []).filter((n) => n !== as.value)] } : {}) };
      if (JSON.stringify(items) === JSON.stringify(stored())) return;
      // The row begun is kept from now: no longer drafted when the panel draws the page that holds it.
      const begun = index >= stored().length;
      if (begun) drafting = false;
      if (!designer.setSetWhen(id, items) && begun) drafting = true;
    }
    // A number or a date is kept as its box is left (its commit); anything else as it is picked or typed.
    if (!number && !date) value.addEventListener(value.tagName === 'SELECT' ? 'change' : 'input', save);
    say.addEventListener('click', () => open(say.getAttribute('aria-expanded') !== 'true'));
    remove.addEventListener('click', () => {
      if (index >= stored().length) {
        drafting = false;
        draw();
        add.focus();
        return;
      }
      const items = stored().filter((_, i) => i !== index);
      designer.setSetWhen(id, items.length ? items : null);
      add.focus();
    });
    body.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      open(false);
      say.focus();
    });

    function open(on: boolean) {
      if (on) for (const other of rows) if (other.element !== element) other.open(false);
      say.setAttribute('aria-expanded', String(on));
      body.hidden = !on;
      element.classList.toggle('fd-answer-rule-open', on);
    }

    return {
      element,
      kind: raw ? 'raw' : def.type,
      open,
      focusFirst: () => value.focus(),
      update(item) {
        const sentence = item ? (pageRules(page, words).find((r) => r.kind === 'set' && r.field === own?.name && r.index === index)?.sentence ?? '') : w.setToWhen;
        say.textContent = sentence;
        remove.setAttribute('aria-label', item ? w.removeSet(sentence) : w.removeNewRule);
        const doc = element.ownerDocument;
        if (item && number) {
          const literal = literalOf(item.value)?.value;
          typedNumber = typeof literal === 'number' ? literal : null;
          number.refresh();
        } else if (item && date) {
          const literal = literalOf(item.value)?.value;
          typedDate = typeof literal === 'string' ? literal : null;
          date.refresh();
        } else if (item && doc.activeElement !== value) value.value = rawFrom(item.value);
        if (item) when = item.when ?? '';
        whenBox.update(page, item ? (item.when ?? '') : when);
        // The fields it can be set on a change of: every other one an expression reads, by its label.
        const fields = Object.entries(page.fields).filter(([name, f]) => name !== own?.name && f.type !== 'one2many');
        const key = JSON.stringify(fields.map(([name, f]) => [name, f.label]));
        if (key !== offered) {
          offered = key;
          as.replaceChildren(as.options[0], ...fields.map(([name, f]) => el('option', { value: name }, w.whenChanges(f.label || name))));
        }
        if (item && doc.activeElement !== as) as.value = item.on?.[0] ?? '';
        whenBox.input.placeholder = as.value ? w.ifPlaceholder : w.whenPlaceholder;
      },
    };
  }

  function draw() {
    if (!own) return;
    const items = stored();
    const count = items.length + (drafting ? 1 : 0);
    const def = own.def;
    rows = Array.from({ length: count }, (_, i) => {
      const item = items[i] ?? null;
      const raw = !!item && literalOf(item.value) === null;
      const kind = raw ? 'raw' : def.type;
      const was = rows[i];
      const view = was && was.kind === kind ? was : row(i, def, raw);
      view.update(item);
      return view;
    });
    rows.forEach((view, i) => {
      if (list.children[i] !== view.element) list.insertBefore(view.element, list.children[i] ?? null);
    });
    while (list.children.length > rows.length) list.lastElementChild?.remove();
    list.hidden = !rows.length;
    add.hidden = drafting || !!def.compute;
  }

  return {
    element,
    update(next) {
      page = next;
      own = ownOf(page, id);
      element.hidden = !own || !SET.has(own.def.type) || (own.def.type === 'selection' && !!own.def.multiple) || designer.isFromModel(id) || (!!own.def.compute && !own.def.setWhen);
      if (!own || element.hidden) return;
      draw();
    },
  };
}
